import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, FileText, User, Calendar, DollarSign, Edit, Trash2, Mail, Search, Camera, CheckCircle, XCircle, Clock, Eye, Flag } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

export default function EventRegistrationsModal({ open, onOpenChange, eventId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [updating, setUpdating] = useState({});
  const [sendingReminders, setSendingReminders] = useState(false);
  const [sendingPaymentReminders, setSendingPaymentReminders] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Rally Master state
  const [eventRallyMasters, setEventRallyMasters] = useState([]);
  const [assigningRallyMaster, setAssigningRallyMaster] = useState(null);
  
  // Dorsal state
  const [editingDorsal, setEditingDorsal] = useState(null);
  const [dorsalValue, setDorsalValue] = useState('');
  
  // Tracking photos state
  const [showTrackingPhotos, setShowTrackingPhotos] = useState(false);
  const [trackingPhotos, setTrackingPhotos] = useState([]);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [verifyingPhoto, setVerifyingPhoto] = useState(null);
  const [selectedTrackingRegistration, setSelectedTrackingRegistration] = useState(null);
  
  const [addFormData, setAddFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    dni: '',
    birth_date: '',
    physical_address: '',
    moto_brand: '',
    moto_model: '',
    moto_plate: '',
    moto_years: '',
    moto_kilometers: '',
    payment_status: 'pending',
    discount_code: ''
  });
  const [files, setFiles] = useState({
    circulation_permit: null,
    technical_sheet: null,
    insurance: null
  });

  useEffect(() => {
    if (open && eventId) {
      fetchRegistrations();
    }
  }, [open, eventId]);

  useEffect(() => {
    if (data) {
      applyFilter();
    }
  }, [filterStatus, data]);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/events/${eventId}/registrations`);
      setData(response.data);
      setFilteredRegistrations(response.data.registrations);
    } catch (error) {
      toast.error('Error al cargar inscripciones');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    if (!data) return;
    
    if (filterStatus === 'all') {
      setFilteredRegistrations(data.registrations);
    } else {
      setFilteredRegistrations(
        data.registrations.filter(reg => reg.payment_status === filterStatus)
      );
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/admin/users`, {
        params: { search: query }
      });
      setSearchResults(response.data);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const handleUserSearch = (e) => {
    const query = e.target.value;
    setUserSearch(query);
    searchUsers(query);
  };

  const selectUser = async (user) => {
    // Auto-fill form with user data
    setAddFormData({
      ...addFormData,
      full_name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || '',
      birth_date: user.birth_date || '',
    });

    // Get user's motorcycles if any
    try {
      const motorcyclesResponse = await axios.get(`${API}/motorcycles`, {
        params: { user_id: user.id }
      });
      
      if (motorcyclesResponse.data && motorcyclesResponse.data.length > 0) {
        const moto = motorcyclesResponse.data[0]; // Use first motorcycle
        setAddFormData(prev => ({
          ...prev,
          moto_brand: moto.brand || '',
          moto_model: moto.model || '',
          moto_plate: moto.plate || '',
          moto_years: moto.year || '',
          moto_kilometers: moto.kilometers || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching user motorcycles:', error);
    }

    setUserSearch(user.email);
    setShowSearchResults(false);
    toast.success(`Datos de ${user.full_name} cargados`);
  };

  const updatePaymentStatus = async (registrationId, newStatus) => {
    setUpdating(prev => ({ ...prev, [registrationId]: true }));
    try {
      await axios.put(
        `${API}/admin/registrations/${registrationId}/payment-status`,
        null,
        { params: { payment_status: newStatus } }
      );
      
      toast.success(`Estado actualizado a: ${newStatus}`);
      
      // Update local state
      setData(prevData => ({
        ...prevData,
        statistics: {
          ...prevData.statistics,
          paid: newStatus === 'paid' ? prevData.statistics.paid + 1 : prevData.statistics.paid - 1,
          pending: newStatus === 'pending' ? prevData.statistics.pending + 1 : prevData.statistics.pending - 1
        },
        registrations: prevData.registrations.map(reg =>
          reg.id === registrationId ? { ...reg, payment_status: newStatus } : reg
        )
      }));
    } catch (error) {
      toast.error('Error al actualizar estado de pago');
      console.error(error);
    } finally {
      setUpdating(prev => ({ ...prev, [registrationId]: false }));
    }
  };

  const updateDorsal = async (registrationId) => {
    try {
      setUpdating(prev => ({ ...prev, [`dorsal_${registrationId}`]: true }));
      await axios.put(
        `${API}/rally-master/registrations/${registrationId}/dorsal`,
        null,
        { params: { dorsal: dorsalValue || null } }
      );
      toast.success(dorsalValue ? `Dorsal #${dorsalValue} asignado` : 'Dorsal eliminado');
      
      // Update local state
      setData(prevData => ({
        ...prevData,
        registrations: prevData.registrations.map(reg =>
          reg.id === registrationId ? { ...reg, dorsal: dorsalValue || null } : reg
        )
      }));
      setFilteredRegistrations(prev => 
        prev.map(reg => reg.id === registrationId ? { ...reg, dorsal: dorsalValue || null } : reg)
      );
      setEditingDorsal(null);
      setDorsalValue('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar dorsal');
      console.error(error);
    } finally {
      setUpdating(prev => ({ ...prev, [`dorsal_${registrationId}`]: false }));
    }
  };

  const handleAddRegistration = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData();
      formData.append('registration_data', JSON.stringify(addFormData));
      
      if (files.circulation_permit) formData.append('circulation_permit', files.circulation_permit);
      if (files.technical_sheet) formData.append('technical_sheet', files.technical_sheet);
      if (files.insurance) formData.append('insurance', files.insurance);
      
      await axios.post(`${API}/admin/events/${eventId}/registrations/manual`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Inscripción añadida exitosamente');
      setShowAddForm(false);
      
      // Reset form
      setAddFormData({
        full_name: '',
        email: '',
        phone: '',
        dni: '',
        birth_date: '',
        physical_address: '',
        moto_brand: '',
        moto_model: '',
        moto_plate: '',
        moto_years: '',
        moto_kilometers: '',
        payment_status: 'pending',
        discount_code: ''
      });
      setFiles({
        circulation_permit: null,
        technical_sheet: null,
        insurance: null
      });
      
      // Refresh data
      fetchRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir inscripción');
      console.error(error);
    }
  };

  const handleEditRegistration = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData();
      formData.append('registration_data', JSON.stringify(addFormData));
      
      if (files.circulation_permit) formData.append('circulation_permit', files.circulation_permit);
      if (files.technical_sheet) formData.append('technical_sheet', files.technical_sheet);
      if (files.insurance) formData.append('insurance', files.insurance);
      
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/registrations/${editingRegistration.id}`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success('Inscripción actualizada exitosamente');
      setShowEditForm(false);
      setEditingRegistration(null);
      
      // Reset form
      setAddFormData({
        full_name: '',
        email: '',
        phone: '',
        dni: '',
        birth_date: '',
        physical_address: '',
        moto_brand: '',
        moto_model: '',
        moto_plate: '',
        moto_years: '',
        moto_kilometers: '',
        payment_status: 'pending',
        discount_code: ''
      });
      setFiles({
        circulation_permit: null,
        technical_sheet: null,
        insurance: null
      });
      
      // Refresh data
      fetchRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar inscripción');
      console.error(error);
    }
  };

  const handleDeleteRegistration = async (registrationId, fullName) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar la inscripción de ${fullName}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/registrations/${registrationId}`);
      toast.success('Inscripción eliminada exitosamente');
      fetchRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar inscripción');
      console.error(error);
    }
  };

  const openEditForm = (registration) => {
    setEditingRegistration(registration);
    setAddFormData({
      full_name: registration.full_name,
      email: registration.email,
      phone: registration.phone,
      dni: registration.dni || '',
      birth_date: registration.birth_date,
      physical_address: registration.physical_address,
      moto_brand: registration.moto_brand,
      moto_model: registration.moto_model,
      moto_plate: registration.moto_plate,
      moto_years: registration.moto_years || '',
      moto_kilometers: registration.moto_kilometers || '',
      payment_status: registration.payment_status,
      discount_code: ''
    });
    setShowEditForm(true);
  };

  const exportToExcel = () => {
    if (!filteredRegistrations.length) {
      toast.error('No hay datos para exportar');
      return;
    }

    // Prepare data for Excel
    const excelData = filteredRegistrations.map(reg => ({
      'Dorsal': reg.dorsal || '-',
      'Nombre Completo': reg.full_name,
      'DNI': reg.dni,
      'Fecha de Nacimiento': reg.birth_date,
      'Email': reg.email,
      'Teléfono': reg.phone || 'N/A',
      'Dirección': reg.physical_address,
      'Marca Moto': reg.moto_brand,
      'Modelo Moto': reg.moto_model,
      'Matrícula': reg.moto_plate,
      'Año Moto': reg.moto_years || 'N/A',
      'Kilómetros': reg.moto_kilometers || 'N/A',
      'Estado de Pago': reg.payment_status,
      'Precio Final': `${reg.final_price}€`,
      'Descuento': reg.discount_applied ? `${reg.discount_applied.percentage}%` : 'No',
      'Fecha Inscripción': new Date(reg.created_at).toLocaleDateString('es-ES')
    }));

    // Create workbook
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Inscritos');

    // Auto-size columns
    const maxWidth = excelData.reduce((w, r) => Math.max(w, ...Object.values(r).map(v => String(v).length)), 10);
    worksheet['!cols'] = Object.keys(excelData[0]).map(() => ({ wch: Math.min(maxWidth, 30) }));

    // Download file
    const filename = `${data.event.title.replace(/\s+/g, '_')}_inscritos_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, filename);
    
    toast.success('Excel exportado correctamente');
  };

  const handleDownloadAcceptance = async (registrationId, fullName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/registrations/${registrationId}/acceptance-document`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (fullName || 'participante').replace(/\s+/g, '_');
      const eventTitle = data?.event?.title || 'evento';
      const safeEvent = eventTitle.replace(/\s+/g, '_');
      link.setAttribute('download', `aceptacion_${safeName}_${safeEvent}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Documento de aceptación descargado');
    } catch (error) {
      console.error('Error downloading acceptance document:', error);
      toast.error('Error al descargar documento de aceptación');
    }
  };

  const handleSendReminders = async () => {
    if (!data || !eventId) return;
    
    const confirmed = window.confirm(
      `¿Estás seguro de que quieres enviar recordatorios a ${data.statistics.total_registrations} participantes?\n\nSe enviará un email a todos los inscritos (excepto los cancelados).`
    );
    
    if (!confirmed) return;
    
    setSendingReminders(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/events/${eventId}/send-reminders`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      toast.success(
        `Recordatorios enviados correctamente. Enviados: ${response.data.sent}, Fallidos: ${response.data.failed}`
      );
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar recordatorios');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleSendPaymentReminders = async () => {
    if (!data || !eventId) return;
    
    const pendingCount = data.statistics.pending || 0;
    
    if (pendingCount === 0) {
      toast.info('No hay inscripciones con pago pendiente');
      return;
    }
    
    const confirmed = window.confirm(
      `¿Estás seguro de que quieres enviar recordatorios de PAGO a ${pendingCount} participantes?\n\nSolo se enviará a los que tienen pago pendiente.`
    );
    
    if (!confirmed) return;
    
    setSendingPaymentReminders(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/events/${eventId}/send-payment-reminder`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      toast.success(
        `Recordatorios de pago enviados. Enviados: ${response.data.sent_count}, Pendientes: ${response.data.pending_count}`
      );
    } catch (error) {
      console.error('Error sending payment reminders:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar recordatorios de pago');
    } finally {
      setSendingPaymentReminders(false);
    }
  };

  // Tracking photos functions
  const openTrackingPhotosModal = async (registration) => {
    setSelectedTrackingRegistration(registration);
    setShowTrackingPhotos(true);
    setLoadingTracking(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/admin/registrations/${registration.id}/tracking-photos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrackingPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error loading tracking photos:', error);
      toast.error('Error al cargar fotos de seguimiento');
      setTrackingPhotos([]);
    } finally {
      setLoadingTracking(false);
    }
  };

  const handleVerifyPhoto = async (photoId, newStatus) => {
    setVerifyingPhoto(photoId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/tracking-photos/${photoId}/verify`,
        null,
        { 
          params: { status: newStatus },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`Foto marcada como ${newStatus === 'verified' ? 'verificada' : newStatus === 'rejected' ? 'rechazada' : 'pendiente'}`);
      
      // Update local state
      setTrackingPhotos(prev => prev.map(photo => 
        photo.id === photoId ? { ...photo, status: newStatus } : photo
      ));
    } catch (error) {
      console.error('Error verifying photo:', error);
      toast.error('Error al actualizar estado de la foto');
    } finally {
      setVerifyingPhoto(null);
    }
  };

  const getTrackingStats = (registrationId) => {
    // This would need the photos data per registration - for now show button
    return null;
  };

  // Rally Master assignment function
  const handleAddRallyMaster = async (userEmail, fullName) => {
    if (!window.confirm(`¿Asignar a ${fullName} como Rally Master de este evento?`)) {
      return;
    }
    
    setAssigningRallyMaster(userEmail);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/rally-masters`,
        null,
        {
          params: { event_id: eventId, user_email: userEmail },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(response.data.message || `${fullName} asignado como Rally Master`);
      
      // Refresh registrations to update UI
      fetchRegistrations();
    } catch (error) {
      console.error('Error assigning rally master:', error);
      const errorMsg = error.response?.data?.detail || 'Error al asignar Rally Master';
      toast.error(errorMsg);
    } finally {
      setAssigningRallyMaster(null);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando inscripciones...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!data) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            Inscritos: {data.event.title}
          </DialogTitle>
          <p className="text-sm text-gray-600">
            {data.event.start_date} - {data.event.end_date}
          </p>
        </DialogHeader>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 my-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-gray-600">Total Inscritos</p>
            </div>
            <p className="text-2xl font-bold text-blue-600">{data.statistics.total_registrations}</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-600" />
              <p className="text-sm text-gray-600">Plazas Libres</p>
            </div>
            <p className="text-2xl font-bold text-green-600">{data.statistics.available_spots}</p>
          </div>
          
          <div className="bg-emerald-50 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              <p className="text-sm text-gray-600">Pagados</p>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{data.statistics.paid}</p>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-yellow-600" />
              <p className="text-sm text-gray-600">Pendientes</p>
            </div>
            <p className="text-2xl font-bold text-yellow-600">{data.statistics.pending}</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-gray-600" />
              <p className="text-sm text-gray-600">Cancelados</p>
            </div>
            <p className="text-2xl font-bold text-gray-600">{data.statistics.cancelled}</p>
          </div>
        </div>

        {/* Filters and Export */}
        <div className="flex flex-wrap gap-4 items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Filtrar por estado:</label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Estado de pago" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="paid">Pagado</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowAddForm(true)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <User className="w-4 h-4 mr-2" />
              Añadir Inscrito
            </Button>
            <Button 
              onClick={handleSendReminders}
              disabled={sendingReminders || data.statistics.total_registrations === 0}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
            >
              <Mail className="w-4 h-4 mr-2" />
              {sendingReminders ? 'Enviando...' : 'Enviar Recordatorios'}
            </Button>
            <Button 
              onClick={handleSendPaymentReminders}
              disabled={sendingPaymentReminders || (data.statistics.pending || 0) === 0}
              className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400"
              title={`Enviar recordatorio de pago a ${data.statistics.pending || 0} inscritos pendientes`}
            >
              <DollarSign className="w-4 h-4 mr-2" />
              {sendingPaymentReminders ? 'Enviando...' : `Recordar Pago (${data.statistics.pending || 0})`}
            </Button>
            <Button 
              onClick={exportToExcel}
              className="bg-green-600 hover:bg-green-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar a Excel
            </Button>
          </div>
        </div>

        {/* Registrations Table */}
        {filteredRegistrations.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600">No hay inscripciones con este filtro</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-2 py-2 text-left font-semibold">Dorsal</th>
                  <th className="px-2 py-2 text-left font-semibold">Nombre</th>
                  <th className="px-2 py-2 text-left font-semibold">DNI</th>
                  <th className="px-2 py-2 text-left font-semibold">Email</th>
                  <th className="px-2 py-2 text-left font-semibold">Teléfono</th>
                  <th className="px-2 py-2 text-left font-semibold">Moto</th>
                  <th className="px-2 py-2 text-left font-semibold">Pago</th>
                  <th className="px-2 py-2 text-left font-semibold">Precio</th>
                  <th className="px-2 py-2 text-left font-semibold">Docs</th>
                  {data.event.requires_odometer_photo && (
                    <th className="px-2 py-2 text-left font-semibold">📸 Km</th>
                  )}
                  {data.event.requires_tracking && (
                    <th className="px-2 py-2 text-left font-semibold">📍 Seguim.</th>
                  )}
                  {(data.event.requires_iba_number || data.event.requires_arem_number || data.event.requires_aemotur_number || data.event.requires_tshirt_size) && (
                    <th className="px-2 py-2 text-left font-semibold">📋 Extras</th>
                  )}
                  <th className="px-2 py-2 text-left font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredRegistrations.map((reg) => (
                  <tr key={reg.id} className="border-b hover:bg-gray-50">
                    {/* Dorsal column */}
                    <td className="px-2 py-2">
                      {editingDorsal === reg.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={dorsalValue}
                            onChange={(e) => setDorsalValue(e.target.value)}
                            placeholder="#"
                            className="w-14 px-1 py-1 text-xs border rounded text-center font-bold"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') updateDorsal(reg.id);
                              if (e.key === 'Escape') {
                                setEditingDorsal(null);
                                setDorsalValue('');
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => updateDorsal(reg.id)}
                            disabled={updating[`dorsal_${reg.id}`]}
                            className="h-6 w-6 p-0 text-green-600"
                          >
                            ✓
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingDorsal(null);
                              setDorsalValue('');
                            }}
                            className="h-6 w-6 p-0 text-red-600"
                          >
                            ✗
                          </Button>
                        </div>
                      ) : (
                        <div
                          onClick={() => {
                            setEditingDorsal(reg.id);
                            setDorsalValue(reg.dorsal || '');
                          }}
                          className="cursor-pointer hover:bg-gray-100 rounded px-2 py-1 text-center min-w-[40px]"
                          title="Clic para editar dorsal"
                        >
                          {reg.dorsal ? (
                            <span className="font-bold text-lg text-purple-700">#{reg.dorsal}</span>
                          ) : (
                            <span className="text-gray-400 text-xs">Sin dorsal</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div>
                        <p className="font-medium text-xs">{reg.full_name}</p>
                        <p className="text-xs text-gray-500">Fecha Nac.: {reg.birth_date}</p>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-xs">{reg.dni || '-'}</td>
                    <td className="px-2 py-2">
                      <p className="text-xs truncate max-w-[120px]" title={reg.email}>{reg.email}</p>
                    </td>
                    <td className="px-2 py-2 text-xs">{reg.phone || '-'}</td>
                    <td className="px-2 py-2">
                      <div>
                        <p className="font-medium text-xs">{reg.moto_brand} {reg.moto_model}</p>
                        <p className="text-xs text-gray-500">{reg.moto_plate}</p>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <select
                        value={reg.payment_status}
                        onChange={(e) => updatePaymentStatus(reg.id, e.target.value)}
                        disabled={updating[reg.id]}
                        className={`w-full px-1 py-1 rounded text-xs border font-medium cursor-pointer focus:outline-none ${
                          reg.payment_status === 'paid' 
                            ? 'border-green-500 bg-green-50 text-green-700' 
                            : reg.payment_status === 'pending' 
                            ? 'border-yellow-500 bg-yellow-50 text-yellow-700' 
                            : 'border-gray-400 bg-gray-50 text-gray-700'
                        } ${updating[reg.id] ? 'opacity-50' : ''}`}
                      >
                        <option value="pending">⏳ Pend.</option>
                        <option value="paid">✓ Pagado</option>
                        <option value="cancelled">✗ Cancel.</option>
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <p className="font-medium text-xs">{reg.final_price}€</p>
                      {reg.discount_applied && (
                        <p className="text-xs text-green-600">-{reg.discount_applied.percentage}%</p>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex gap-1">
                        {reg.circulation_permit && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const url = reg.circulation_permit.startsWith('http') ? reg.circulation_permit : `${API.replace('/api', '')}/api${reg.circulation_permit}`;
                              window.open(url, '_blank');
                            }}
                            className="h-6 w-6 p-0"
                            title="Permiso circulación"
                          >
                            <FileText className="w-3 h-3" />
                          </Button>
                        )}
                        {reg.technical_sheet && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const url = reg.technical_sheet.startsWith('http') ? reg.technical_sheet : `${API.replace('/api', '')}/api${reg.technical_sheet}`;
                              window.open(url, '_blank');
                            }}
                            className="h-6 w-6 p-0"
                            title="Ficha técnica"
                          >
                            <FileText className="w-3 h-3 text-blue-500" />
                          </Button>
                        )}
                        {reg.insurance && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const url = reg.insurance.startsWith('http') ? reg.insurance : `${API.replace('/api', '')}/api${reg.insurance}`;
                              window.open(url, '_blank');
                            }}
                            className="h-6 w-6 p-0"
                            title="Seguro"
                          >
                            <FileText className="w-3 h-3 text-green-500" />
                          </Button>
                        )}
                        {!reg.circulation_permit && !reg.technical_sheet && !reg.insurance && (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    {/* Odometer Photos Column */}
                    {data.event.requires_odometer_photo && (
                      <td className="px-2 py-2">
                        {reg.odometer_motorcycles && reg.odometer_motorcycles.length > 0 ? (
                          <div className="space-y-1">
                            {reg.odometer_motorcycles.map((moto, idx) => (
                              <div key={`${moto.brand}-${moto.model}-${idx}`} className="flex items-center gap-1 text-xs">
                                <span className="truncate max-w-[80px]" title={`${moto.brand} ${moto.model}`}>
                                  {moto.brand} {moto.model}
                                </span>
                                <span className="text-gray-500">{moto.kilometers}km</span>
                                {moto.photo_url && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => window.open(moto.photo_url, '_blank')}
                                    className="h-5 w-5 p-0"
                                    title="Ver foto"
                                  >
                                    <Camera className="w-3 h-3 text-blue-500" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    )}
                    {/* Tracking Photos Column */}
                    {data.event.requires_tracking && (
                      <td className="px-2 py-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openTrackingPhotosModal(reg)}
                          className="h-7 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                          title="Ver fotos de seguimiento"
                          data-testid={`view-tracking-${reg.id}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Ver
                        </Button>
                      </td>
                    )}
                    {/* Extra Data Column */}
                    {(data.event.requires_iba_number || data.event.requires_arem_number || data.event.requires_aemotur_number || data.event.requires_tshirt_size) && (
                      <td className="px-2 py-2">
                        <div className="text-xs space-y-0.5">
                          {data.event.requires_iba_number && reg.iba_number && (
                            <p><span className="text-gray-500">IBA:</span> {reg.iba_number}</p>
                          )}
                          {data.event.requires_arem_number && reg.arem_number && (
                            <p><span className="text-gray-500">AREM:</span> {reg.arem_number}</p>
                          )}
                          {data.event.requires_aemotur_number && reg.aemotur_number && (
                            <p><span className="text-gray-500">AEMOTUR:</span> {reg.aemotur_number}</p>
                          )}
                          {data.event.requires_tshirt_size && reg.tshirt_size && (
                            <p><span className="text-gray-500">Talla:</span> {reg.tshirt_size}</p>
                          )}
                          {!reg.iba_number && !reg.arem_number && !reg.aemotur_number && !reg.tshirt_size && (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-2 py-2">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleAddRallyMaster(reg.email, reg.full_name)}
                          disabled={assigningRallyMaster === reg.email}
                          className="h-6 w-6 p-0"
                          title="Asignar como Rally Master"
                        >
                          <Flag className={`w-3 h-3 ${assigningRallyMaster === reg.email ? 'text-gray-400 animate-pulse' : 'text-purple-600'}`} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadAcceptance(reg.id, reg.full_name)}
                          className="h-6 w-6 p-0"
                          title="Descargar aceptación"
                        >
                          <FileText className="w-3 h-3 text-green-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditForm(reg)}
                          className="h-6 w-6 p-0"
                          title="Editar"
                        >
                          <Edit className="w-3 h-3 text-blue-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteRegistration(reg.id, reg.full_name)}
                          className="h-6 w-6 p-0"
                          title="Eliminar"
                        >
                          <Trash2 className="w-3 h-3 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 text-sm text-gray-500 text-right">
          Mostrando {filteredRegistrations.length} de {data.statistics.total_registrations} inscripciones
        </div>
      </DialogContent>
    </Dialog>

    {/* Add Registration Form Dialog */}
    <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Añadir Inscrito Manualmente</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleAddRegistration} className="space-y-4">
          {/* User Search */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Search className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-900">Buscar Usuario Registrado</h3>
            </div>
            <p className="text-sm text-blue-700 mb-3">
              Busca un usuario existente para auto-completar sus datos
            </p>
            <div className="relative">
              <input
                type="text"
                value={userSearch}
                onChange={handleUserSearch}
                placeholder="Escribe email o nombre..."
                className="w-full px-3 py-2 border border-blue-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => selectUser(user)}
                      className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                    >
                      <div className="font-semibold text-gray-900">{user.full_name}</div>
                      <div className="text-sm text-gray-600">{user.email}</div>
                      {user.phone && <div className="text-xs text-gray-500">📞 {user.phone}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Personal Data */}
          <div className="border-b pb-4">
            <h3 className="font-semibold mb-3">Datos Personales</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nombre Completo *</label>
                <input
                  type="text"
                  value={addFormData.full_name}
                  onChange={(e) => setAddFormData({...addFormData, full_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={addFormData.email}
                  onChange={(e) => setAddFormData({...addFormData, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Teléfono *</label>
                <input
                  type="tel"
                  value={addFormData.phone}
                  onChange={(e) => setAddFormData({...addFormData, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">DNI {data?.event?.requires_dni === true && '*'}</label>
                <input
                  type="text"
                  value={addFormData.dni}
                  onChange={(e) => setAddFormData({...addFormData, dni: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required={data?.event?.requires_dni === true}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Fecha de Nacimiento *</label>
                <input
                  type="date"
                  value={addFormData.birth_date}
                  onChange={(e) => setAddFormData({...addFormData, birth_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Dirección</label>
                <input
                  type="text"
                  value={addFormData.physical_address}
                  onChange={(e) => setAddFormData({...addFormData, physical_address: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
          </div>

          {/* Motorcycle Data */}
          <div className="border-b pb-4">
            <h3 className="font-semibold mb-3">Datos de la Moto</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Marca *</label>
                <input
                  type="text"
                  value={addFormData.moto_brand}
                  onChange={(e) => setAddFormData({...addFormData, moto_brand: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Modelo *</label>
                <input
                  type="text"
                  value={addFormData.moto_model}
                  onChange={(e) => setAddFormData({...addFormData, moto_model: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Matrícula *</label>
                <input
                  type="text"
                  value={addFormData.moto_plate}
                  onChange={(e) => setAddFormData({...addFormData, moto_plate: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Año</label>
                <input
                  type="number"
                  value={addFormData.moto_years}
                  onChange={(e) => setAddFormData({...addFormData, moto_years: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="2020"
                  min="1900"
                  max="2099"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Kilómetros</label>
                <input
                  type="number"
                  value={addFormData.moto_kilometers}
                  onChange={(e) => setAddFormData({...addFormData, moto_kilometers: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="50000"
                />
              </div>
            </div>
          </div>

          {/* Documents */}
          {data?.event?.requires_documents !== false && (
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Documentación de la Moto</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Permiso de Circulación</label>
                  <input
                    type="file"
                    onChange={(e) => setFiles({...files, circulation_permit: e.target.files[0]})}
                    className="w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Ficha Técnica</label>
                  <input
                    type="file"
                    onChange={(e) => setFiles({...files, technical_sheet: e.target.files[0]})}
                    className="w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Seguro</label>
                  <input
                    type="file"
                    onChange={(e) => setFiles({...files, insurance: e.target.files[0]})}
                    className="w-full text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payment */}
          <div className="border-b pb-4">
            <h3 className="font-semibold mb-3">Estado de Pago</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Estado *</label>
                <select
                  value={addFormData.payment_status}
                  onChange={(e) => setAddFormData({...addFormData, payment_status: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                >
                  <option value="pending">Pendiente</option>
                  <option value="paid">Pagado</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Código de Descuento</label>
                <input
                  type="text"
                  value={addFormData.discount_code}
                  onChange={(e) => setAddFormData({...addFormData, discount_code: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Opcional"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
              Añadir Inscripción
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    {/* Edit Registration Form Dialog */}
    <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Inscripción</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleEditRegistration} className="space-y-4">
          {/* Personal Data */}
          <div className="border-b pb-4">
            <h3 className="font-semibold mb-3">Datos Personales</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nombre Completo *</label>
                <input
                  type="text"
                  value={addFormData.full_name}
                  onChange={(e) => setAddFormData({...addFormData, full_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={addFormData.email}
                  onChange={(e) => setAddFormData({...addFormData, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Teléfono *</label>
                <input
                  type="tel"
                  value={addFormData.phone}
                  onChange={(e) => setAddFormData({...addFormData, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">DNI {data?.event?.requires_dni === true && '*'}</label>
                <input
                  type="text"
                  value={addFormData.dni}
                  onChange={(e) => setAddFormData({...addFormData, dni: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required={data?.event?.requires_dni === true}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Fecha de Nacimiento *</label>
                <input
                  type="date"
                  value={addFormData.birth_date}
                  onChange={(e) => setAddFormData({...addFormData, birth_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Dirección</label>
                <input
                  type="text"
                  value={addFormData.physical_address}
                  onChange={(e) => setAddFormData({...addFormData, physical_address: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
          </div>

          {/* Motorcycle Data */}
          <div className="border-b pb-4">
            <h3 className="font-semibold mb-3">Datos de la Moto</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Marca *</label>
                <input
                  type="text"
                  value={addFormData.moto_brand}
                  onChange={(e) => setAddFormData({...addFormData, moto_brand: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Modelo *</label>
                <input
                  type="text"
                  value={addFormData.moto_model}
                  onChange={(e) => setAddFormData({...addFormData, moto_model: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Matrícula *</label>
                <input
                  type="text"
                  value={addFormData.moto_plate}
                  onChange={(e) => setAddFormData({...addFormData, moto_plate: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Año</label>
                <input
                  type="number"
                  value={addFormData.moto_years}
                  onChange={(e) => setAddFormData({...addFormData, moto_years: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="2020"
                  min="1900"
                  max="2099"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Kilómetros</label>
                <input
                  type="number"
                  value={addFormData.moto_kilometers}
                  onChange={(e) => setAddFormData({...addFormData, moto_kilometers: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="50000"
                />
              </div>
            </div>
          </div>

          {/* Documents */}
          {data?.event?.requires_documents !== false && (
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Documentación de la Moto</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Permiso de Circulación</label>
                  <input
                    type="file"
                    onChange={(e) => setFiles({...files, circulation_permit: e.target.files[0]})}
                    className="w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Ficha Técnica</label>
                  <input
                    type="file"
                    onChange={(e) => setFiles({...files, technical_sheet: e.target.files[0]})}
                    className="w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Seguro</label>
                  <input
                    type="file"
                    onChange={(e) => setFiles({...files, insurance: e.target.files[0]})}
                    className="w-full text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payment */}
          <div className="border-b pb-4">
            <h3 className="font-semibold mb-3">Estado de Pago</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Estado *</label>
                <select
                  value={addFormData.payment_status}
                  onChange={(e) => setAddFormData({...addFormData, payment_status: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                >
                  <option value="pending">Pendiente</option>
                  <option value="paid">Pagado</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Código de Descuento</label>
                <input
                  type="text"
                  value={addFormData.discount_code}
                  onChange={(e) => setAddFormData({...addFormData, discount_code: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Opcional"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setShowEditForm(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
              Actualizar Inscripción
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    {/* Tracking Photos Modal */}
    <Dialog open={showTrackingPhotos} onOpenChange={setShowTrackingPhotos}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-blue-600" />
            Fotos de Seguimiento
          </DialogTitle>
          <DialogDescription>
            {selectedTrackingRegistration?.full_name} - {selectedTrackingRegistration?.email}
          </DialogDescription>
        </DialogHeader>

        {loadingTracking ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando fotos...</p>
            </div>
          </div>
        ) : trackingPhotos.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Camera className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600">Este participante no ha subido fotos de seguimiento</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stats summary */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{trackingPhotos.length}</p>
                <p className="text-xs text-gray-600">Total Fotos</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {trackingPhotos.filter(p => p.status === 'verified').length}
                </p>
                <p className="text-xs text-gray-600">Verificadas</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">
                  {trackingPhotos.filter(p => p.status === 'pending').length}
                </p>
                <p className="text-xs text-gray-600">Pendientes</p>
              </div>
            </div>

            {/* Photos grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {trackingPhotos.map((photo) => (
                <div key={photo.id} className="relative group border rounded-lg overflow-hidden bg-white shadow-sm">
                  {/* Image */}
                  <div 
                    className="cursor-pointer"
                    onClick={() => window.open(photo.photo_url, '_blank')}
                  >
                    <img
                      src={photo.photo_url}
                      alt={photo.description || 'Foto de seguimiento'}
                      className="w-full h-40 object-cover"
                    />
                  </div>
                  
                  {/* Status Badge */}
                  <div className="absolute top-2 left-2">
                    <Badge
                      className={
                        photo.status === 'verified' ? 'bg-green-500 text-white' :
                        photo.status === 'rejected' ? 'bg-red-500 text-white' :
                        'bg-yellow-500 text-white'
                      }
                    >
                      {photo.status === 'verified' && <CheckCircle className="w-3 h-3 mr-1" />}
                      {photo.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
                      {photo.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                      {photo.status === 'verified' ? 'Verificada' : 
                       photo.status === 'rejected' ? 'Rechazada' : 'Pendiente'}
                    </Badge>
                  </div>

                  {/* Description */}
                  {photo.description && (
                    <div className="p-2 bg-gray-50 border-t">
                      <p className="text-xs text-gray-700 truncate" title={photo.description}>
                        {photo.description}
                      </p>
                    </div>
                  )}

                  {/* Upload date */}
                  <div className="px-2 py-1 text-xs text-gray-500">
                    {new Date(photo.uploaded_at).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>

                  {/* Action buttons */}
                  <div className="p-2 border-t flex gap-2">
                    <Button
                      size="sm"
                      variant={photo.status === 'verified' ? 'default' : 'outline'}
                      className={`flex-1 h-7 text-xs ${photo.status === 'verified' ? 'bg-green-600 hover:bg-green-700' : 'border-green-500 text-green-600 hover:bg-green-50'}`}
                      onClick={() => handleVerifyPhoto(photo.id, photo.status === 'verified' ? 'pending' : 'verified')}
                      disabled={verifyingPhoto === photo.id}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {photo.status === 'verified' ? 'Verificada' : 'Verificar'}
                    </Button>
                    <Button
                      size="sm"
                      variant={photo.status === 'rejected' ? 'default' : 'outline'}
                      className={`flex-1 h-7 text-xs ${photo.status === 'rejected' ? 'bg-red-600 hover:bg-red-700' : 'border-red-500 text-red-600 hover:bg-red-50'}`}
                      onClick={() => handleVerifyPhoto(photo.id, photo.status === 'rejected' ? 'pending' : 'rejected')}
                      disabled={verifyingPhoto === photo.id}
                    >
                      <XCircle className="w-3 h-3 mr-1" />
                      {photo.status === 'rejected' ? 'Rechazada' : 'Rechazar'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
